import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { applyDemoFilter } from '@/lib/demoFilter';

interface CarrierDashboardStats {
  trucksPosted: number;
  activeTrucksOnboard: number;
  activeLoadsOnboard: number;
  trucksOpen: number;
  trucksExpired: number;
  isLoading: boolean;
}

interface CarrierFilters {
  timeRange?: 'Current' | '1 Day' | '7 Day' | '30 Day';
  enabled?: boolean;
}

const fetchCarrierStats = async (userId: string, timeRange?: string, isDemoUser: boolean = false) => {
  const today = new Date().toISOString().split('T')[0];
  const timeFilter = timeRange && timeRange !== 'Current' ? getTimeRangeFilter(timeRange) : null;

  let userTrucksQuery = supabase
    .from('truck_postings')
    .select('id, status, available_from, available_to, created_at')
    .eq('created_by', userId);

  userTrucksQuery = applyDemoFilter(userTrucksQuery, isDemoUser);

  if (timeFilter) {
    userTrucksQuery = userTrucksQuery.gte('created_at', timeFilter);
  }

  let platformTrucksQuery = supabase.from('truck_postings').select('*', { count: 'exact', head: true }).in('status', ['active', 'available']).gte('available_to', today);
  platformTrucksQuery = applyDemoFilter(platformTrucksQuery, isDemoUser);

  let platformLoadsQuery = supabase.from('loads').select('*', { count: 'exact', head: true }).eq('status', 'posted').eq('is_public', true);
  platformLoadsQuery = applyDemoFilter(platformLoadsQuery, isDemoUser);

  const [{ data: userTrucks }, { count: platformTrucksCount }, { count: platformLoadsCount }] = await Promise.all([
    userTrucksQuery,
    platformTrucksQuery,
    platformLoadsQuery,
  ]);

  const trucks = userTrucks || [];

  return {
    trucksPosted: trucks.length,
    activeTrucksOnboard: platformTrucksCount ?? 0,
    activeLoadsOnboard: platformLoadsCount ?? 0,
    trucksOpen: trucks.filter(t => (t.status === 'active' || t.status === 'available') && t.available_to >= today).length,
    trucksExpired: trucks.filter(t => (t.status === 'active' || t.status === 'available') && t.available_to < today).length,
  };
};

export const useCarrierDashboardStats = (filters?: CarrierFilters): CarrierDashboardStats => {
  const { user, isDemoUser } = useAuth();

  const isEnabled = (filters?.enabled ?? true) && !!user;

  const { data, isLoading } = useQuery({
    queryKey: ['carrier-dashboard-stats', user?.id, filters?.timeRange, isDemoUser],
    queryFn: () => fetchCarrierStats(user!.id, filters?.timeRange, isDemoUser),
    enabled: isEnabled,
    staleTime: 30_000,
  });

  return {
    trucksPosted: data?.trucksPosted ?? 0,
    activeTrucksOnboard: data?.activeTrucksOnboard ?? 0,
    activeLoadsOnboard: data?.activeLoadsOnboard ?? 0,
    trucksOpen: data?.trucksOpen ?? 0,
    trucksExpired: data?.trucksExpired ?? 0,
    isLoading,
  };
};

const getTimeRangeFilter = (timeRange: string): string => {
  const now = new Date();
  switch (timeRange) {
    case '1 Day':
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      return today.toISOString();
    case '7 Day':
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      return weekAgo.toISOString();
    case '30 Day':
      const monthAgo = new Date(now);
      monthAgo.setDate(now.getDate() - 30);
      return monthAgo.toISOString();
    case 'Current':
    default:
      return new Date(0).toISOString();
  }
};
